import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { AdSlot } from "@/components/web/AdSlot";
import { fetchPost } from "@/lib/blogApi";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

const md = {
  h2: (p) => <h2 className="font-heading font-bold text-2xl text-white mt-12 mb-4" {...p} />,
  h3: (p) => <h3 className="font-heading font-bold text-xl text-white mt-8 mb-3" {...p} />,
  p: (p) => <p className="text-zinc-300 leading-relaxed mb-5" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 text-zinc-300 leading-relaxed mb-5 space-y-2" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 text-zinc-300 leading-relaxed mb-5 space-y-2" {...p} />,
  a: ({ href, ...p }) =>
    href && href.startsWith("/") ? (
      <Link to={href} className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200" {...p} />
    ) : (
      <a href={href} rel="noopener noreferrer nofollow" target="_blank" className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200" {...p} />
    ),
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-[#00C5FE] pl-5 text-zinc-400 italic my-6" {...p} />,
  code: (p) => <code className="font-mono-ls text-sm bg-white/5 px-1.5 py-0.5 text-[#00C5FE]" {...p} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    fetchPost(slug).then(setPost).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <section className="py-32 text-center">
        <h1 data-testid="blog-post-not-found" className="font-heading font-black text-3xl text-white">Article not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#00C5FE] hover:text-white font-heading font-semibold transition-colors duration-200">
          Back to the blog <ArrowRight size={16} />
        </Link>
      </section>
    );
  if (!post)
    return (
      <section className="py-32 text-center">
        <p className="font-mono-ls text-xs tracking-[0.2em] uppercase text-zinc-400">Loading article…</p>
      </section>
    );

  return (
    <>
      <Seo title={post.seo_title || `${post.title} — LinkExpln`} description={post.seo_description || post.excerpt} image={post.featured_image || undefined} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Article",
          headline: post.title,
          description: post.seo_description || post.excerpt,
          author: { "@type": "Organization", name: post.author || "LinkExpln Team" },
          publisher: { "@type": "Organization", name: "LinkExpln", logo: { "@type": "ImageObject", url: "https://linkexpln.com/icon-512.png" } },
          datePublished: post.published_at, dateModified: post.updated_at,
          mainEntityOfPage: post.canonical_url,
          ...(post.featured_image ? { image: post.featured_image.startsWith("/") ? `https://linkexpln.com${post.featured_image}` : post.featured_image } : {}),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://linkexpln.com/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://linkexpln.com/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: post.canonical_url },
          ],
        })}</script>
      </Helmet>

      <article className="pt-24 pb-24 sm:pt-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <nav aria-label="Breadcrumb" data-testid="post-breadcrumb" className="mb-8 font-mono-ls text-[11px] tracking-[0.15em] uppercase text-zinc-400">
            <Link to="/" className="hover:text-[#00C5FE] transition-colors duration-200">Home</Link>
            <span className="mx-2 text-zinc-600">/</span>
            <Link to="/blog" className="hover:text-[#00C5FE] transition-colors duration-200">Blog</Link>
            <span className="mx-2 text-zinc-600">/</span>
            <span className="text-[#00C5FE]">{post.category}</span>
          </nav>
          <p className="font-mono-ls text-[11px] tracking-[0.2em] uppercase text-[#00C5FE] mb-4">{post.category}</p>
          <h1 data-testid="post-title" className="font-heading font-black tracking-tighter text-white text-3xl sm:text-5xl leading-tight text-balance">
            {post.title}
          </h1>
          <p className="mt-5 font-mono-ls text-[11px] tracking-[0.15em] uppercase text-zinc-400">
            {post.author} · Published {fmtDate(post.published_at)}
            {post.updated_at && post.updated_at.slice(0, 10) !== (post.published_at || "").slice(0, 10) && ` · Updated ${fmtDate(post.updated_at)}`}
          </p>
          {post.featured_image && (
            <img src={post.featured_image} alt={post.title} className="mt-10 w-full border border-white/10" />
          )}

          <div data-testid="post-content" className="mt-10">
            <ReactMarkdown components={md}>{post.content}</ReactMarkdown>
          </div>

          <AdSlot id="post-bottom" className="mt-14" />

          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="px-3 py-1 border border-white/15 font-mono-ls text-[10px] tracking-[0.15em] uppercase text-zinc-400">{t}</span>
              ))}
            </div>
          )}

          <div className="mt-14 border border-white/10 p-8">
            <p className="ls-overline mb-3">Stay Protected</p>
            <p className="text-zinc-300 leading-relaxed">
              Not sure about a link you received? Check it with the{" "}
              <Link to="/scanner" className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200">LinkExpln scanner</Link>{" "}
              or learn <Link to="/how-it-works" className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200">how LinkExpln works</Link>.
            </p>
            <Link to="/blog" data-testid="post-back-to-blog" className="mt-5 inline-flex items-center gap-2 text-white hover:text-[#00C5FE] font-heading font-semibold text-sm transition-colors duration-200">
              More articles <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
